import React, { useState, useEffect } from 'react'
import { lumi } from '@/lib/lumi'
import { useSATCalculations } from '@/hooks/useSATCalculations'
import { useSuperscores } from '@/hooks/useSuperscores'
import { socialShare } from '@/lib/socialShare'

// Official Digital SAT Math conversion table (44 questions, raw 0-44 → scaled 200-800)
const mathConversion: { [key: number]: number } = {
  0: 200, 1: 200, 2: 200, 3: 200, 4: 210, 5: 230, 6: 250, 7: 270, 8: 290, 9: 310,
  10: 330, 11: 350, 12: 360, 13: 380, 14: 390, 15: 410, 16: 420, 17: 440, 18: 450,
  19: 460, 20: 470, 21: 480, 22: 490, 23: 500, 24: 510, 25: 520, 26: 530, 27: 540,
  28: 550, 29: 560, 30: 570, 31: 590, 32: 610, 33: 630, 34: 650, 35: 670, 36: 690,
  37: 710, 38: 730, 39: 750, 40: 760, 41: 770, 42: 780, 43: 790, 44: 800
}

// Official Digital SAT Reading & Writing conversion table (54 questions, raw 0-54 → scaled 200-800)
const readingConversion: { [key: number]: number } = {
  0: 200, 1: 200, 2: 200, 3: 200, 4: 210, 5: 220, 6: 230, 7: 250, 8: 260, 9: 280,
  10: 290, 11: 300, 12: 310, 13: 320, 14: 330, 15: 340, 16: 350, 17: 360, 18: 370,
  19: 380, 20: 390, 21: 400, 22: 410, 23: 420, 24: 430, 25: 440, 26: 450, 27: 460,
  28: 470, 29: 480, 30: 490, 31: 500, 32: 510, 33: 520, 34: 530, 35: 540, 36: 550,
  37: 560, 38: 570, 39: 580, 40: 590, 41: 610, 42: 620, 43: 640, 44: 650, 45: 670,
  46: 680, 47: 700, 48: 710, 49: 730, 50: 740, 51: 760, 52: 770, 53: 780, 54: 800
}

export function DigitalSATCalculator() {
  const [mathRaw, setMathRaw] = useState<string>('')
  const [readingRaw, setReadingRaw] = useState<string>('')
  const [superscore, setSuperscore] = useState(false)
  const [mathRaw2, setMathRaw2] = useState<string>('')
  const [readingRaw2, setReadingRaw2] = useState<string>('')
  const { saveCalculation } = useSATCalculations()
  const { saveSuperscore } = useSuperscores()

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('digitalSATData')
    if (saved) {
      try {
        const data = JSON.parse(saved)
        setMathRaw(data.mathRaw || '')
        setReadingRaw(data.readingRaw || '')
        setSuperscore(data.superscore || false)
        setMathRaw2(data.mathRaw2 || '')
        setReadingRaw2(data.readingRaw2 || '')
      } catch (e) {
        console.error('Failed to load saved data:', e)
      }
    }
  }, [])

  // Save to localStorage whenever values change
  useEffect(() => {
    const data = { mathRaw, readingRaw, superscore, mathRaw2, readingRaw2 }
    localStorage.setItem('digitalSATData', JSON.stringify(data))
  }, [mathRaw, readingRaw, superscore, mathRaw2, readingRaw2])

  const calculateScore = (raw: string, conversion: { [key: number]: number }, maxRaw: number) => {
    const num = parseInt(raw)
    if (isNaN(num) || num < 0 || num > maxRaw) return 0
    return conversion[num] || 200
  }

  const mathScore1 = calculateScore(mathRaw, mathConversion, 44)
  const readingScore1 = calculateScore(readingRaw, readingConversion, 54)
  const totalScore1 = mathScore1 + readingScore1

  const mathScore2 = superscore ? calculateScore(mathRaw2, mathConversion, 44) : 0
  const readingScore2 = superscore ? calculateScore(readingRaw2, readingConversion, 54) : 0
  
  const superscoreTotal = superscore 
    ? Math.max(mathScore1, mathScore2) + Math.max(readingScore1, readingScore2)
    : 0

  const handleSaveCalculation = async () => {
    const user = lumi.auth.user
    if (!user) {
      alert('Please sign in to save your calculation')
      return
    }

    try {
      if (!superscore && mathScore1 > 0 && readingScore1 > 0) {
        await saveCalculation({
          calculationType: 'digital_sat',
          mathRaw: parseInt(mathRaw),
          readingRaw: parseInt(readingRaw),
          mathScaled: mathScore1,
          readingScaled: readingScore1,
          totalScore: totalScore1
        })
        alert('Score saved successfully!')
      } else if (superscore && superscoreTotal > 0) {
        await saveSuperscore({
          test1MathRaw: parseInt(mathRaw),
          test1ReadingRaw: parseInt(readingRaw),
          test2MathRaw: parseInt(mathRaw2),
          test2ReadingRaw: parseInt(readingRaw2),
          bestMathScaled: Math.max(mathScore1, mathScore2),
          bestReadingScaled: Math.max(readingScore1, readingScore2),
          superscoreTotal
        })
        alert('Superscore saved successfully!')
      }
    } catch (error) {
      console.error('Failed to save:', error)
      alert('Failed to save calculation')
    }
  }

  const handleEmailReport = async () => {
    const user = lumi.auth.user
    if (!user) {
      alert('Please sign in to email your report')
      return
    }

    if ((!superscore && totalScore1 <= 400) || (superscore && superscoreTotal <= 400)) {
      alert('Please enter valid scores before emailing')
      return
    }

    try {
      const emailData: any = {
        mathRaw: parseInt(mathRaw),
        readingRaw: parseInt(readingRaw),
        mathScaled: mathScore1,
        readingScaled: readingScore1,
        totalScore: superscore ? superscoreTotal : totalScore1,
        calculationType: 'digital_sat'
      }

      if (superscore) {
        emailData.superscore = {
          test1Math: mathScore1,
          test1Reading: readingScore1,
          test2Math: mathScore2,
          test2Reading: readingScore2,
          superscoreTotal
        }
      }

      await lumi.functions.invoke('sendScoreReport', {
        method: 'POST',
        body: emailData
      })

      alert(`Score report sent to ${user.email}!`)
    } catch (error: any) {
      console.error('Failed to send email:', error)
      const errorMessage = error?.data?.error || 'Failed to send email report'
      alert(errorMessage)
    }
  }

  const handleDownloadPDF = async () => {
    const user = lumi.auth.user
    if (!user) {
      alert('Please sign in to download your report')
      return
    }

    if ((!superscore && totalScore1 <= 400) || (superscore && superscoreTotal <= 400)) {
      alert('Please enter valid scores before downloading')
      return
    }

    try {
      const pdfData: any = {
        mathRaw: parseInt(mathRaw),
        readingRaw: parseInt(readingRaw),
        mathScaled: mathScore1,
        readingScaled: readingScore1,
        totalScore: superscore ? superscoreTotal : totalScore1,
        calculationType: 'digital_sat'
      }

      if (superscore) {
        pdfData.superscore = {
          test1Math: mathScore1,
          test1Reading: readingScore1,
          test2Math: mathScore2,
          test2Reading: readingScore2,
          superscoreTotal
        }
      }

      const result = await lumi.functions.invoke('generatePDFReport', {
        method: 'POST',
        body: pdfData
      })

      // Create blob and trigger download
      const blob = new Blob([result], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `SAT-Score-Report-${pdfData.totalScore}.pdf`
      a.click()
      URL.revokeObjectURL(url)

      alert('PDF report downloaded successfully!')
    } catch (error: any) {
      console.error('Failed to generate PDF:', error)
      const errorMessage = error?.data?.error || 'Failed to generate PDF report'
      alert(errorMessage)
    }
  }

  const handleCopyLink = async () => {
    const shareData = {
      totalScore: superscore ? superscoreTotal : totalScore1,
      calculationType: 'digital_sat',
      mathScore: superscore ? Math.max(mathScore1, mathScore2) : mathScore1,
      readingScore: superscore ? Math.max(readingScore1, readingScore2) : readingScore1,
      superscore: superscore ? true : undefined
    }
    const success = await socialShare.copyLink(shareData)
    if (success) {
      alert('Link copied to clipboard!')
    } else {
      alert('Failed to copy link')
    }
  }

  return (
    <section className="bg-white rounded-lg shadow-md p-6 md:p-8" aria-labelledby="digital-sat-heading">
      <h1 id="digital-sat-heading" className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
        Free Digital SAT Score Calculator
      </h1>
      <p className="text-gray-600 mb-6">
        Calculate your SAT score quickly and accurately. Enter your raw scores below to estimate your SAT score and see your results instantly.
      </p>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Test 1 */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-800">
            {superscore ? 'Test 1 Scores' : 'Your Test Scores'}
          </h3>
          <div>
            <label htmlFor="math-raw" className="block text-sm font-medium text-gray-700 mb-2">
              Math Raw Score (0-44)
            </label>
            <input
              id="math-raw"
              type="number"
              min="0"
              max="44"
              value={mathRaw}
              onChange={(e) => setMathRaw(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter number correct"
              aria-describedby="math-raw-hint"
            />
            <p id="math-raw-hint" className="text-sm text-gray-500 mt-1">
              Scaled Score: {mathScore1 > 0 ? mathScore1 : '---'}
            </p>
          </div>

          <div>
            <label htmlFor="reading-raw" className="block text-sm font-medium text-gray-700 mb-2">
              Reading & Writing Raw Score (0-54)
            </label>
            <input
              id="reading-raw"
              type="number"
              min="0"
              max="54"
              value={readingRaw}
              onChange={(e) => setReadingRaw(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter number correct"
              aria-describedby="reading-raw-hint"
            />
            <p id="reading-raw-hint" className="text-sm text-gray-500 mt-1">
              Scaled Score: {readingScore1 > 0 ? readingScore1 : '---'}
            </p>
          </div>
        </div>

        {/* Test 2 (Superscore) */}
        {superscore && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">Test 2 Scores</h3>
            <div>
              <label htmlFor="math-raw-2" className="block text-sm font-medium text-gray-700 mb-2">
                Math Raw Score (0-44)
              </label>
              <input
                id="math-raw-2"
                type="number"
                min="0"
                max="44"
                value={mathRaw2}
                onChange={(e) => setMathRaw2(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter number correct"
                aria-describedby="math-raw-2-hint"
              />
              <p id="math-raw-2-hint" className="text-sm text-gray-500 mt-1">
                Scaled Score: {mathScore2 > 0 ? mathScore2 : '---'}
              </p>
            </div>

            <div>
              <label htmlFor="reading-raw-2" className="block text-sm font-medium text-gray-700 mb-2">
                Reading & Writing Raw Score (0-54)
              </label>
              <input
                id="reading-raw-2"
                type="number"
                min="0"
                max="54"
                value={readingRaw2}
                onChange={(e) => setReadingRaw2(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter number correct"
                aria-describedby="reading-raw-2-hint"
              />
              <p id="reading-raw-2-hint" className="text-sm text-gray-500 mt-1">
                Scaled Score: {readingScore2 > 0 ? readingScore2 : '---'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Superscore Toggle */}
      <div className="mt-6 flex items-center">
        <input
          id="superscore-toggle"
          type="checkbox"
          checked={superscore}
          onChange={(e) => setSuperscore(e.target.checked)}
          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          aria-describedby="superscore-description"
        />
        <label htmlFor="superscore-toggle" className="ml-2 text-sm font-medium text-gray-700">
          Calculate Superscore (SAT Superscore Calculator)
        </label>
      </div>
      <p id="superscore-description" className="text-xs text-gray-500 ml-6 mt-1">
        Combine your best section scores from multiple test dates
      </p>

      {/* Results */}
      <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-900">Your SAT Score</h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleSaveCalculation}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              aria-label="Save calculation to your account"
            >
              💾 Save
            </button>
            <button
              onClick={handleEmailReport}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
              aria-label="Email score report to your email"
            >
              📧 Email
            </button>
            <button
              onClick={handleDownloadPDF}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
              aria-label="Download PDF score report"
            >
              📄 PDF
            </button>
          </div>
        </div>
        
        {/* Social Share Buttons */}
        {((superscore && superscoreTotal > 400) || (!superscore && totalScore1 > 400)) && (
          <div className="flex flex-wrap justify-center gap-2 mb-4 pb-4 border-b border-gray-200">
            <button
              onClick={() => socialShare.twitter({
                totalScore: superscore ? superscoreTotal : totalScore1,
                calculationType: 'digital_sat',
                mathScore: superscore ? Math.max(mathScore1, mathScore2) : mathScore1,
                readingScore: superscore ? Math.max(readingScore1, readingScore2) : readingScore1
              })}
              className="px-3 py-1.5 bg-black text-white rounded hover:bg-gray-800 transition-colors text-xs font-medium"
              aria-label="Share on Twitter"
            >
              𝕏 Share
            </button>
            <button
              onClick={() => socialShare.facebook({
                totalScore: superscore ? superscoreTotal : totalScore1,
                calculationType: 'digital_sat'
              })}
              className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-xs font-medium"
              aria-label="Share on Facebook"
            >
              📘 Facebook
            </button>
            <button
              onClick={() => socialShare.linkedin({
                totalScore: superscore ? superscoreTotal : totalScore1,
                calculationType: 'digital_sat',
                mathScore: superscore ? Math.max(mathScore1, mathScore2) : mathScore1,
                readingScore: superscore ? Math.max(readingScore1, readingScore2) : readingScore1
              })}
              className="px-3 py-1.5 bg-blue-700 text-white rounded hover:bg-blue-800 transition-colors text-xs font-medium"
              aria-label="Share on LinkedIn"
            >
              💼 LinkedIn
            </button>
            <button
              onClick={() => socialShare.whatsapp({
                totalScore: superscore ? superscoreTotal : totalScore1,
                calculationType: 'digital_sat',
                mathScore: superscore ? Math.max(mathScore1, mathScore2) : mathScore1,
                readingScore: superscore ? Math.max(readingScore1, readingScore2) : readingScore1
              })}
              className="px-3 py-1.5 bg-green-500 text-white rounded hover:bg-green-600 transition-colors text-xs font-medium"
              aria-label="Share on WhatsApp"
            >
              💬 WhatsApp
            </button>
            <button
              onClick={handleCopyLink}
              className="px-3 py-1.5 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors text-xs font-medium"
              aria-label="Copy link to clipboard"
            >
              🔗 Copy Link
            </button>
          </div>
        )}
        {!superscore ? (
          <div className="text-center">
            <div className="text-5xl font-bold text-blue-600 mb-2">
              {totalScore1 > 400 ? totalScore1 : '---'}
            </div>
            <div className="text-sm text-gray-600">Total Score (400-1600)</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-700 mb-2">
                {totalScore1 > 400 ? totalScore1 : '---'}
              </div>
              <div className="text-sm text-gray-600">Test 1 Total</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">
                {superscoreTotal > 400 ? superscoreTotal : '---'}
              </div>
              <div className="text-sm text-gray-600">Superscore</div>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
